import {useMemo} from 'react';
import styled from 'styled-components';
import {IOrganization, useAdminOrganizations} from '../../API';
import {Body2, BoldSubhead1, H6, Theme} from '../../Components';
import {AdminPanel} from './Layout';
import {getConfig} from '../../getConfig';

const {subscriptionManagementEnabled} = getConfig();

const Header = styled(BoldSubhead1)`
  margin-bottom: 8px;
`;

const SummaryDetailBlock = styled.div`
  // display: flex;
  // flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  flex: 1;
`;

const Circle = styled.div<{percentage?: number}>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  min-width: 120px;
  max-width: 120px;
  max-height: 120px;
  border-radius: 50%;
  margin-bottom: 16px;
  position: relative;
  background: conic-gradient(
    ${Theme.Colors.Blues._000} ${(props) => (props.percentage || 0) * 3.6}deg,
    transparent ${(props) => (props.percentage || 0) * 3.6}deg
  );
  &::before {
    content: '';
    position: absolute;
    width: 90%;
    height: 90%;
    border-radius: 50%;
    background: white;
  }
`;

const SummaryDetail = ({label, value}: {label: string; value?: string}) => {
  return (
    <SummaryDetailBlock>
      <Body2 style={{marginBottom: 4}}>
        <span style={{}}>{label}</span>: <span style={{fontWeight: 'bold', fontSize: 16}}>{value || '-'}</span>
      </Body2>
    </SummaryDetailBlock>
  );
};

const SummaryBigDetail = ({label, value, totalValue}: {label: string; value?: string | number; totalValue?: number}) => {
  const percentage = totalValue && value ? (Number(value) / totalValue) * 100 : 0;

  return (
    <SummaryDetailBlock>
      <Header>{label} </Header>
      <Circle percentage={percentage}>
        <H6 style={{margin: 0, fontSize: 32, position: 'relative', zIndex: 1}}>{value || '-'} </H6>
      </Circle>
    </SummaryDetailBlock>
  );
};

export const AdminSummaryBlock = () => {
  const {data: organizations} = useAdminOrganizations();
  const activeSubscriptionOrgs = useMemo(() => organizations?.filter((u) => !!u.subscription), [organizations])?.length;

  const activeOrgs = useMemo(() => organizations?.filter((u) => !!u.status && u.status === 'active'), [organizations])?.length;

  const noSubscriptions = useMemo(() => organizations?.filter((u) => !u.subscription)?.length, [organizations]);

  const inactiveOrgs = useMemo(() => organizations?.filter((u) => !!u.status && u.status !== 'active'), [organizations])?.length;

  const subscriptionTypeCounts = useMemo(() => {
    return organizations?.reduce((acc: any, o: IOrganization) => {
      if (o.subscription?.type) {
        acc[o.subscription?.type] = (acc[o.subscription?.type] || 0) + 1;
      }
      return acc;
    }, {});
  }, [organizations]);

  const orgTypeCounts = useMemo(() => {
    return organizations?.reduce((acc: any, o: IOrganization) => {
      acc[o.subscription_type || 'unknown'] = (acc[o.subscription_type || 'unknown'] || 0) + 1;
      return acc;
    }, {});
  }, [organizations]);

  // collect unique counts of subscription types using reduce
  const subscriptionStatusCounts = useMemo(() => {
    return organizations?.reduce((acc: any, o: IOrganization) => {
      if (o.subscription?.status) {
        acc[o.subscription?.status] = (acc[o.subscription?.status] || 0) + 1;
      }
      return acc;
    }, {});
  }, [organizations]);

  return (
    <AdminPanel style={{overflow: 'unset', flex: 0}}>
      <div style={{display: 'flex', flexWrap: 'wrap', flexDirection: 'row', gap: 24}}>
        {subscriptionManagementEnabled ? (
          <>
            <SummaryDetailBlock>
              <Header>Subscription Types</Header>
              {Object.keys(subscriptionTypeCounts || {}).map((key) => {
                return <SummaryDetail label={key} value={subscriptionTypeCounts[key]} />;
              })}
            </SummaryDetailBlock>

            <SummaryDetailBlock>
              <Header>Subscription Status</Header>
              {Object.keys(subscriptionStatusCounts || {}).map((key) => {
                return <SummaryDetail label={key} value={subscriptionStatusCounts[key]} />;
              })}
            </SummaryDetailBlock>

            <SummaryBigDetail label="Total Organizations" value={organizations?.length?.toString()} />
            <SummaryBigDetail label="Active Subscriptions" value={activeSubscriptionOrgs} />
            <SummaryBigDetail label="No Subscriptions" value={noSubscriptions} />
          </>
        ) : (
          <>
            <SummaryDetailBlock>
              <Header>Organization Types</Header>
              {Object.keys(orgTypeCounts || {})
                .sort()
                .map((key) => {
                  return <SummaryDetail label={key} value={orgTypeCounts[key]} />;
                })}
            </SummaryDetailBlock>

            <SummaryBigDetail label="Total Organizations" value={organizations?.length?.toString()} totalValue={organizations?.length} />
            <SummaryBigDetail label="Active" value={activeOrgs} totalValue={organizations?.length} />
            <SummaryBigDetail label="Inactive" value={inactiveOrgs} totalValue={organizations?.length} />
          </>
        )}
      </div>
    </AdminPanel>
  );
};
